import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Grid, Typography, TextField, Button, Stack, Select, MenuItem, FormHelperText } from '@mui/material'
import PasswordChangeModal from '@components/Modal/PasswordChangeModal'
import WithdrawModal from './WithdrawModal'
import OrgSelectModal from '@components/Modal/OrgSelectModal'
import { useIsMobile } from '@/hooks/Responsive'
import { memberDetail, updateMember } from "@api/myPageAccount";
import { cmmCd } from "@api/comp";
import { formatYyyyMmDd, fmtTel } from "@utils/FormatUtil";
import { useConfirm } from '@components/ConfirmProvider'
import { useAuth } from '@components/AuthContext';
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

function InfoRow({ label, children, isMobile }) {
  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'flex-start' : 'center'}
      sx={{
        mb: 3,
        gap: isMobile ? 1 : 5,
      }}
    >
      <Box sx={{ width: isMobile ? '100%' : '160px' }}>
        <Typography fontWeight={500}>
          {label}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
        {typeof children === 'string' ? (
          <Typography>
            {children}
          </Typography>
        ) : (
          children
        )}
      </Box>
    </Stack>
  )
}

export default function MyAccount() {

  const { user, loading, isAuthReady } = useAuth();

  const { isMobile } = useIsMobile()
  const lang = useLang() 
  const navigate = useNavigate()

  const [openOrg, setOpenOrg] = useState(false)
  const [org, setOrg] = useState(null)

  const [detail, setDetail] = useState('');  // 기관상세정보

  const [openPassword, setOpenPassword] = useState(false)

  const [openWithdrawModal, setOpenWithdrawModal] = useState(false)

  const [status, setStatus] = useState('')

  const handleSubmitWithdraw = (reason) => {
    // 탈퇴 로직 처리
    setOpenWithdrawModal(false)
  }

  const { alert, confirm } = useConfirm();
  
  const [mbrClasList, setMbrClasList] = useState([]);  // 기관회원분류 목록
  const [detailInfo, setDetailInfo] = useState({});  // 상세정보
  const [loadOrgInfo, setLoadOrgInfo] = useState({});  // 기관정보(조회정보)
  const [errors, setErrors] = useState({ memberInstCode: '', detail: '' })

  useEffect(() => {
    // 아직 로딩 중이면 아무 것도 하지 않음
    if (loading) return;

    //기관회원분류 목록조회
    callMbrClasList();
    // 회원 상세정보 조회
    onLoad();
  }, [loading, user])

  // 코드목록조회(기관회원분류)
  const callMbrClasList = async () => {
      try {
        const response = await cmmCd('MBR_CLAS');
        setMbrClasList(response?.data.filter(item => item.comCd !== "GENERAL"));
      } catch (error) {
        //console.error('코드목록조회 불러오기 실패:', error);
      }
  };

  // 회원 상세정보 조회
  const onLoad = async () => {
    // user 체크
    if (!user) {
      return;
    }

    try {
      const params = {
        publicId : user.pid,
      };
      const response = await memberDetail(params);

      if (response?.success) {
        setDetailInfo({
          publicId: response?.data?.publicId,
          registDttm: formatYyyyMmDd(response?.data?.registDttm),
          memberKindName: response?.data?.memberKindName,
          memberEmail: response?.data?.memberEmail,
          memberName: response?.data?.memberName,
          cellphoneNo: fmtTel(response?.data?.cellphoneNo),
          memberInstCode: response?.data?.memberInstCode,
          insttNm: response?.data?.insttNm,
          lowestInsttNm: response?.data?.lowestInsttNm,
          memberClssCode: response?.data?.memberClssCode,
          memberClssName: response?.data?.memberClssName,
          memberTypeCode: response?.data?.memberTypeCode,
        });

        setLoadOrgInfo({
          memberInstCode: response?.data?.memberInstCode,
          insttNm: response?.data?.insttNm,
          lowestInsttNm: response?.data?.lowestInsttNm,
        });
        
        // 기관상세정보 : '일반'코드는 삭제
        setDetail(response?.data?.memberClssName == '일반' ? '' : response?.data?.memberClssName);

        // 승인상태 체크
        if (response?.data?.permissionYn == 'I') {  // 승인대기
          setStatus('pending');
        }
        else if (response?.data?.permissionYn == 'N') {  // 미승인
          setStatus('rejected');
        }
      }
      else {
        alert(response?.message || "조회 중 오류가 발생했습니다.");
      }
    } catch (e) {
      console.error(e);
      // 401 은 전역 인터셉터에서 "세션 만료" alert를 이미 띄우므로, 여기서는 중복 alert 방지
      if (e?.response?.status === 401) {
        return;
      }
      alert(e?.response?.data?.message || e.message || "조회 중 오류가 발생했습니다.");
    }
    
  }

  // 수정내용 체크
  const validate = () => {
    let tempErrors = { memberInstCode: '', detail: '' }
    if (!detailInfo.memberInstCode) {
      tempErrors.memberInstCode = '* 기관을 선택해 주세요.'
    }
    if (!detail) {
      tempErrors.detail = '* 기관상세정보를 선택해 주세요.'
    }

    setErrors(tempErrors)
    return Object.values(tempErrors).every(x => x === '')
  };

  // 수정내용 저장
  const handleSubmit = async () => {
    if (!validate()) return;

    if (!user) {
      return;
    }

    try {
      const clssCode = mbrClasList.find(item => item.comNm === detail)?.comCd;

      const params = {
        publicId: detailInfo.publicId,
        memberInstCode: detailInfo.memberInstCode,
        memberClssCode: clssCode,
        permissionYn: 'I',
      };

      const response = await updateMember(params);

      if (response?.success) {
        alert("수정되었습니다.");
        // 재조회
        onLoad();
      }
      else {
        alert(response?.message || "수정 중 오류가 발생했습니다.");
      }
    } catch (e) {
      console.error(e);
      // 401 은 전역 인터셉터에서 "세션 만료" alert를 이미 띄우므로, 여기서는 중복 alert 방지
      if (e?.response?.status === 401) {
        return;
      }
      alert(e?.response?.data?.message || e.message || "수정 중 오류가 발생했습니다.");
    }
  };

  // 기관팝업 콜백
  const handleSetOrg = (inst) => {
    setDetailInfo((p) => ({
      ...p,
      memberInstCode: inst.insttId,
      insttNm: inst.insttNm
    }));
  };

  // 초기화
  const reset = () => {
    setDetailInfo((data) => ({
      ...data,
      memberInstCode: '',
      insttNm: ''
    }));
    setDetail('');
  }

  return (
    <Box>
      {/* 기본정보 */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: isMobile ? 2 : 4 }}>
        기본정보
      </Typography>

      <InfoRow label="가입일" isMobile={isMobile}>
        {detailInfo.registDttm}
      </InfoRow>

      <InfoRow label="소셜 계정" isMobile={isMobile}>
        {detailInfo.memberKindName}
      </InfoRow>

      <InfoRow label="이메일 계정" isMobile={isMobile}>
        {detailInfo.memberEmail}
      </InfoRow>

      <InfoRow label="비밀번호" isMobile={isMobile}>
        <Button 
          variant="outlined" 
          color="primary" 
          size="medium"
          onClick={() => setOpenPassword(true)}
        >
          비밀번호 변경
        </Button>
      </InfoRow>
      <PasswordChangeModal
        open={openPassword}
        onClose={() => setOpenPassword(false)}
        id={detailInfo.publicId}
      />

      <InfoRow label="이름" isMobile={isMobile}>
        {detailInfo.memberName}
      </InfoRow>

      <InfoRow label="휴대전화번호" isMobile={isMobile}>
        {detailInfo.cellphoneNo}
      </InfoRow>

      {/* 기관정보 */}
      <Box sx={{ bgcolor: 'grey.50', py: 5, px: isMobile ? 2 : 4, mt: 8, mb: 5, borderRadius: 4 }}>
        <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} sx={{ gap: isMobile ? 1.5 : 3 }}>
          <Typography variant="subtitle1" fontWeight={600}>
          기관정보
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* 상태 표시 */}
            {status === 'pending' ? (
              <Typography>
                <Typography component="span" color="secondary.main" fontWeight={500}>승인대기중</Typography>{` `}
                <Typography component="span">입니다.</Typography>
              </Typography>
            ) : status === 'rejected' ? (
              <Typography>
                <Typography component="span" color="error.main" fontWeight={500}>승인불가</Typography>{` `}
                <Typography component="span">되었습니다.</Typography>
              </Typography>
            ) : null}

            <Button
              variant="outlined"
              size="xsmall"
              color="grey"
              disabled={status === 'pending'} // 대기 중이면 비활성 // 불가 : rejected
              sx={{ width: '55px', minWidth: '55px', whiteSpace: 'nowrap' }}
              onClick={reset}
            >
              초기화
            </Button>
          </Stack>
        </Stack>

        {detailInfo.memberTypeCode === 'GNRL' ? (
          <Typography 
            sx={{ 
              mt: 1,
              mb: 4,
            }}
          >
            기관정보 등록은 해당 기관의 관리자 승인 이후 가능합니다.
          </Typography>
        ) : (
          <></>
        )}

        {detailInfo.memberTypeCode === 'GNRL' ? (
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={9}>
              <Typography fontWeight={500} sx={{ mb: 0.5 }}>
                기관명
              </Typography>
              <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
                <TextField
                  fullWidth
                  placeholder="기관검색을 통해 입력해 주세요"
                  value={detailInfo.insttNm || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  error={!!errors.memberInstCode}
                  helperText={errors.memberInstCode}
                />
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={status === 'pending'} // 대기 중이면 비활성
                    onClick={() => setOpenOrg(true)}
                  >
                    기관검색
                  </Button>
              </Stack>
            </Grid>

            {status === '' || status === 'rejected' ? (
            <Grid item xs={12} md={3}>
              <Typography fontWeight={500} sx={{ mb: 0.5 }}>
                기관상세정보
              </Typography>
              <Select
                displayEmpty
                fullWidth
                value={detail || ''} 
                onChange={(e) => setDetail(e.target.value)}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: 'grey.500', lineHeight: '48px' }}>
                        기관상세정보를 선택 해 주세요
                      </Typography>
                    )
                  }
                  return selected
                }}
                error={!!errors.detail}
              >
              {mbrClasList.map((item) => (
                <MenuItem key={item.comCd} value={item.comNm}>{item.comNm}</MenuItem>
              ))}
              </Select>
              {errors.detail && (<FormHelperText error sx={{ mt: 0.5 }}>{errors.detail}</FormHelperText>)}
            </Grid>
            ) : status === 'pending' ? (
              <Grid item xs={12} md={3}>
                <Typography fontWeight={500} sx={{ mb: 0.5 }}>
                  기관상세정보
                </Typography>
                <Typography sx={{ lineHeight: '40px' }}>{detailInfo.memberClssName}</Typography>
              </Grid>
            ) : null}
           </Grid>
        ) : (
        <>
          <InfoRow label="기관명" isMobile={isMobile}>
            {detailInfo.insttNm}
          </InfoRow>

          <InfoRow label="기관상세정보" isMobile={isMobile}>
            {detailInfo.memberClssName}
          </InfoRow>
        </>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {detailInfo.memberTypeCode === 'GNRL' ? (
            status === 'pending' ? (
              <></>
            ) : (
              <Button variant="contained" color="primary" size="medium" sx={{ width: isMobile ? '100%' : 'auto'}} onClick={() => handleSubmit()}>
                승인요청
              </Button>
            )
          ) : (
          <></>
          )}
        </Stack>

      </Box>

      {/* 기관선택 모달 */}
      <OrgSelectModal
        open={openOrg}
        onClose={() => setOpenOrg(false)}
        onSelect={handleSetOrg}   // 선택된 기관 데이터 가져오기
      />

      {/* 버튼 2개 */}
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button variant="outlined" color="grey" size="large" sx={{ width: isMobile ? '100%' : 'auto'}} onClick={() => setOpenWithdrawModal(true)}>
          회원탈퇴
        </Button>
        <Button variant="outlined" color="primary" size="large" sx={{ width: isMobile ? '100%' : 'auto'}} onClick={() => navigate(buildLangHref('/', lang))}>
          홈
        </Button>
      </Stack>

      {/* 회원탈퇴 모달 */}
      <WithdrawModal
        open={openWithdrawModal}
        onClose={() => setOpenWithdrawModal(false)}
        title="회원탈퇴"
        email={detailInfo.memberEmail}
        org={loadOrgInfo.insttNm}
        id={detailInfo.publicId}
        onSubmit={handleSubmitWithdraw}
      />
    </Box>
  )
}
